import type { ReactNode } from 'react';
import React, { useEffect, useMemo } from 'react';
import classnames from 'clsx';
import styles from './button.scss';
import { createPortal } from 'react-dom';
import { TestIds } from '../../../constants';

export interface Props {
  content: string;
  position?: 'top' | 'right';
  children: ReactNode;
  shouldShow: boolean; // Based on the experiment
}

export default (props: Props) => {
  const { children, content, position = 'top', shouldShow } = props;
  const [isShown, setIsShown] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hideTooltip();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const tooltipPositionStyles = useMemo(() => {
    if (!triggerRef.current || !isShown) {
      return;
    }

    const triggerPosition = triggerRef.current!.getBoundingClientRect();

    switch (position) {
      case 'top':
        return {
          top: triggerPosition.top - 25 + window.scrollY,
          left: triggerPosition.left + triggerPosition.width / 2,
          transform: 'translate(-50%)',
        };
      case 'right':
        return {
          top: triggerPosition.top + window.scrollY,
          left: triggerPosition.left + triggerPosition.width + 10,
        };
    }
  }, [isShown, position]);

  const showTooltip = () => {
    setIsShown(true);
  };

  const hideTooltip = () => {
    setIsShown(false);
  };

  const hideTooltipFromButton = () => {
    setTimeout(() => {
      if (tooltipRef.current && !tooltipRef.current.matches(':hover')) {
        hideTooltip();
      }
    }, 50);
  };

  const hideTooltipFromTooltip = () => {
    setTimeout(() => {
      if (triggerRef.current && !triggerRef.current.matches(':hover')) {
        hideTooltip();
      }
    }, 50);
  };

  if (!shouldShow) {
    return <>{children}</>;
  }

  return (
    <div data-testid={TestIds.tooltip.root}>
      <div
        data-testid={TestIds.tooltip.trigger}
        ref={triggerRef}
        onMouseEnter={() => showTooltip()}
        onMouseLeave={() => hideTooltipFromButton()}
        onFocus={() => showTooltip()}
        onBlur={() => hideTooltip()}
      >
        {children}
      </div>
      {tooltipPositionStyles &&
        isShown &&
        createPortal(
          <div
            data-testid={TestIds.tooltip.content}
            ref={tooltipRef}
            onMouseEnter={() => showTooltip()}
            onMouseLeave={() => hideTooltipFromTooltip()}
            className={classnames(
              styles.tooltip,
              styles[`tooltip-${position}`],
            )}
            style={{
              visibility: isShown ? 'visible' : 'hidden',
              zIndex: 40000,
              position: 'absolute',
              ...tooltipPositionStyles,
            }}
          >
            <p>{content}</p>
          </div>,
          document.body,
        )}
    </div>
  );
};
